import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { KeywordGroup } from '@brand24-original/llm-types';
import { updateKeywordStatus } from '../../../../services/monitor/keywords/KeywordsService';
import { KeywordStatus } from '../../../../models/monitor/KeywordResponse';
import { GET_PROJECT_KEYWORDS } from '../../../query/keyword/keys';
import { invalidateBrandStats } from '../../../query/invalidateBrandStats';

interface UpdateKeywordStatusParams {
  keywordId: number;
  status: KeywordStatus;
}

interface UseUpdateKeywordStatusOptions {
  onError?: (error: unknown) => void;
}

/**
 * Status jednej nazwy marki (`PATCH /monitor/keywords/:id/status`). Przełącznik przestawia się
 * optymistycznie: lista marek wraca po kilku sekundach przy dużych projektach, a bez tego po
 * odpowiedzi serwera przełącznik wracał do starego stanu i przeskakiwał dopiero po odświeżeniu.
 */
export const useUpdateKeywordStatus = (
  projectId: number,
  options?: UseUpdateKeywordStatusOptions
) => {
  const queryClient = useQueryClient();
  const keywordsKey = [GET_PROJECT_KEYWORDS, projectId];

  return useMutation({
    mutationFn: ({ keywordId, status }: UpdateKeywordStatusParams) =>
      updateKeywordStatus(keywordId, status),
    onMutate: async ({ keywordId, status }) => {
      await queryClient.cancelQueries({ queryKey: keywordsKey });
      const previous = queryClient.getQueryData<KeywordGroup[]>(keywordsKey);
      // Grupa niesie dane głównej nazwy, więc jej `status` przestawia się razem z nią.
      queryClient.setQueryData<KeywordGroup[]>(keywordsKey, (groups) =>
        groups?.map((group) => ({
          ...group,
          status: group.id === keywordId ? status : group.status,
          aliases: group.aliases.map((alias) => (alias.id === keywordId ? { ...alias, status } : alias)),
        }))
      );
      return { previous };
    },
    onError: (error: unknown, _params, context) => {
      if (context?.previous) {
        queryClient.setQueryData(keywordsKey, context.previous);
      }
      options?.onError?.(error);
    },
    onSettled: () => {
      void invalidateBrandStats(queryClient);
    },
  });
};
